import { Button, Card, CardContent, Spinner } from '@crm/ui';
import { ChartNoAxesColumn } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useKpis } from '@/modules/management/api';
import { LEADS_ROUTES, useFunnel } from '../api';
import { FunnelChart } from './funnel-chart';
import { type Consulta, FunnelFilters, paramsDaConsulta } from './funnel-filters';
import { PipelineChart } from './pipeline-chart';

/**
 * O recorte inicial: os ultimos 45 dias, de qualquer origem. E a janela que
 * mostra o funil vivo - um mes fechado corta negociacao que comecou no fim do
 * mes passado.
 *
 * Os demais campos ja nascem no mes do painel, para quem trocar de modo nao
 * cair num trimestre aleatorio.
 */
export function consultaInicial(period: { year: number; month: number }): Consulta {
  return {
    modo: 'dias45',
    ano: period.year,
    trimestre: (Math.floor((period.month - 1) / 3) + 1) as 1 | 2 | 3 | 4,
    mes: period.month,
    campaignIds: [],
    gatilho: 'all',
  };
}

/**
 * Funil e pipeline sao a mesma operacao em duas unidades - gente e dinheiro.
 * Por isso dividem o box e o recorte inteiro: periodo, campanha e gatilho
 * valem para os dois, e um risco no meio separa as leituras sem precisar de
 * duas molduras.
 *
 * O rodape recebe o que alimenta esse funil - hoje, as campanhas no ar. Cada
 * bloco que entra ali traz o proprio risco em cima.
 */
export function FunnelWidget({
  consulta,
  onConsultaChange,
  unitId,
  period,
  children,
}: {
  consulta: Consulta;
  onConsultaChange: (consulta: Consulta) => void;
  unitId?: string;
  period: { year: number; month: number };
  /** Rodape do box, sob os graficos. */
  children?: React.ReactNode;
}) {
  const navigate = useNavigate();

  const recorte = paramsDaConsulta(consulta);

  // Duas coisas diferentes com nomes parecidos: a coorte (quem entra na conta)
  // e o primeiro degrau desenhado.
  //
  // Lead organico nunca recebeu disparo - a coorte dele comeca no lead, e o
  // degrau de prospeccao nao existe. Campanha comeca na mensagem que saiu.
  // Com os dois juntos nao ha coorte: exigir que todos tenham passado pela
  // prospeccao apagaria justamente o lead que chegou sozinho. O grafico
  // desenha desde a prospeccao mesmo assim, para o degrau aparecer.
  const organico = consulta.gatilho === 'organic';
  const inicio = organico ? 'LEAD' : 'MESSAGE_SENT';
  const { data, isLoading } = useFunnel({
    ...recorte,
    unitId,
    trigger: consulta.gatilho === 'all' ? undefined : consulta.gatilho,
    startStage: consulta.gatilho === 'all' ? undefined : inicio,
  });

  // O ticket medio que valoriza o pipeline sai do mesmo recorte do funil.
  const kpis = useKpis({
    from: recorte.from ? new Date(recorte.from) : new Date(period.year, 0, 1),
    to: recorte.to ? new Date(recorte.to) : new Date(period.year, 11, 31, 23, 59, 59),
    unitId,
  });

  const irParaEtapa = (stage: string) => navigate(`${LEADS_ROUTES.BOARD}?stage=${stage}`);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight">Graficos de conversao</h2>

          <Button size="sm" variant="ghost" onClick={() => navigate(LEADS_ROUTES.CONVERSION)}>
            <ChartNoAxesColumn className="h-4 w-4" />
            Relatorios
          </Button>
        </div>

        {/* O filtro fica a vista: escondido atras de um botao, o recorte
            passava despercebido e os numeros pareciam ser da tela toda. Um
            risco embaixo separa sem precisar de moldura. */}
        <div className="border-b border-border pb-4">
          <FunnelFilters consulta={consulta} onChange={onConsultaChange} />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <Spinner />
          </div>
        ) : (
          /* O risco so aparece quando as duas colunas estao lado a lado: no
             celular elas viram uma sobre a outra, e ai a linha divide errado. */
          <div className="grid gap-6 lg:grid-cols-2 lg:divide-x lg:divide-border [&>*]:min-w-0">
            <section className="space-y-4 lg:pr-6">
              <h3 className="text-base font-medium">Funil de conversao</h3>
              <FunnelChart
                rows={data?.data ?? []}
                contacts={data?.contacts}
                inicio={inicio}
                onSelectStage={irParaEtapa}
              />
            </section>

            <section className="space-y-4 border-t border-border pt-6 lg:border-t-0 lg:pl-6 lg:pt-0">
              <h3 className="text-base font-medium">Pipeline financeiro</h3>
              <PipelineChart
                rows={data?.data ?? []}
                attended={data?.attended ?? 0}
                averageTicket={kpis.data?.averageTicket ?? 0}
                onSelectStage={irParaEtapa}
              />
            </section>
          </div>
        )}

        {children}
      </CardContent>
    </Card>
  );
}
